import { WHATSAPP_CHANNEL_LABS_FEATURE_ID } from '@syncanix/shared';

import { anchor } from './setting-anchors.types';

import type { SettingAnchor } from './setting-anchors.types';

/**
 * The `/connections` anchors — one per tab.
 *
 * The tab IS the unit here, and that is unusual enough to say why. Everywhere
 * else a tab is a grouping and the cards inside it are the settings; on this hub
 * each tab is a whole kind of connection with its own list, its own credentials
 * and its own empty state, so "where do I set up the MCP server" and "where do I
 * put our LLM provider key" are seven different questions with seven answers.
 * Indexing the page alone would answer all seven with "Connections", which is
 * the find failure this search exists to remove.
 *
 * `integrations:view` throughout — the surface the hub's own nav row claims, and
 * the read that decides whether any of it renders.
 */
export const CONNECTIONS_SETTING_ANCHORS = {
  'connection-mcp': anchor(
    'setting-connection-mcp',
    '/connections',
    'connections.tab.mcp',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionMcp', pane: 'mcp' },
  ),
  'connection-federation': anchor(
    'setting-connection-federation',
    '/connections',
    'connections.tab.federation',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionFederation', pane: 'federation' },
  ),
  'connection-byok': anchor(
    'setting-connection-byok',
    '/connections',
    'connections.tab.byok',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionByok', pane: 'byok' },
  ),
  'connection-byo-endpoint': anchor(
    'setting-connection-byo-endpoint',
    '/connections',
    'connections.tab.byoEndpoint',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionByoEndpoint', pane: 'byo-endpoint' },
  ),
  'connection-origins': anchor(
    'setting-connection-origins',
    '/connections',
    'connections.tab.origins',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionOrigins', pane: 'origins' },
  ),
  'connection-webhooks': anchor(
    'setting-connection-webhooks',
    '/connections',
    'connections.tab.webhooks',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionWebhooks', pane: 'webhooks' },
  ),
  // ADR-0796 — the WhatsApp channel. `settings` rather than `integrations`,
  // because the resolver gates on it: the channel decides where end-user
  // transcripts flow (Meta as a sub-processor), the same class as federation.
  'connection-whatsapp': anchor(
    'setting-connection-whatsapp',
    '/connections',
    'connections.tab.whatsapp',
    'settings',
    'view',
    {
      keywordsKey: 'settingSearch.connectionWhatsapp',
      pane: 'whatsapp',
      // Hidden from ⌘K while the Labs flag is off — the hub hides the tab, and
      // the search must not offer a way into a surface the page will not show.
      labsFlag: WHATSAPP_CHANNEL_LABS_FEATURE_ID,
    },
  ),
  'connection-domain': anchor(
    'setting-connection-domain',
    '/connections',
    'connections.tab.domain',
    'integrations',
    'view',
    { keywordsKey: 'settingSearch.connectionDomain', pane: 'domain' },
  ),
} as const satisfies Record<string, SettingAnchor>;
